import React, { useEffect, useState, useRef } from 'react';
import { XIcon, SendIcon, AlertCircleIcon } from 'lucide-react';
import { ChatMessage } from '../types';
import ReactMarkdown from 'react-markdown';
interface ChatModalProps {
  cardIndex: number;
  cardSubject: string;
  cardQuestion: string;
  messages: ChatMessage[];
  onClose: () => void;
  onSendMessage: (message: string) => void;
}
export function ChatModal({
  cardIndex,
  cardSubject,
  cardQuestion,
  messages,
  onClose,
  onSendMessage
}: ChatModalProps) {
  // Pre-fill with question if this is the first time opening chat (no messages)
  const [input, setInput] = useState(messages.length === 0 ? cardQuestion : '');
  const [isLoading, setIsLoading] = useState(false);
  const [showDomainNotice, setShowDomainNotice] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Check if we're on a deployed domain (not localhost or magicpatterns)
  const isDeployedDomain = !window.location.hostname.includes('localhost') && !window.location.hostname.includes('magicpatterns');
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);
  // Focus textarea when modal opens
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages]);
  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [input]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    const userMessage = input.trim();
    setInput('');
    setIsLoading(true);
    try {
      await onSendMessage(userMessage);
    } finally {
      setIsLoading(false);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
    // Allow Shift+Enter for new line (default behavior)
  };
  return <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={onClose} data-id="element-14">
      <div className="bg-white rounded-lg w-full max-w-2xl h-[80vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-15">
        <div className="flex items-center justify-between p-4 border-b border-gray-200" data-id="element-16">
          <div data-id="element-17">
            <h2 className="font-semibold text-lg" data-id="element-18">Chat about: {cardSubject}</h2>
            <p className="text-sm text-gray-500" data-id="element-19">Card {cardIndex + 1}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 transition-colors" aria-label="Close chat" data-id="element-20">
            <XIcon className="w-6 h-6" data-id="element-21" />
          </button>
        </div>

        {isDeployedDomain && showDomainNotice && <div className="mx-4 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-22">
            <div className="flex items-start gap-2" data-id="element-23">
              <AlertCircleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-24" />
              <div className="flex-1" data-id="element-25">
                <p className="text-sm text-amber-800 font-medium" data-id="element-26">
                  Domain Setup Required
                </p>
                <p className="text-xs text-amber-700 mt-1" data-id="element-27">
                  If you see a popup asking for credentials, your domain needs
                  to be whitelisted. Contact Magic Patterns support with your
                  domain: <strong data-id="element-28">{window.location.origin}</strong>
                </p>
                <button onClick={() => setShowDomainNotice(false)} className="text-xs text-amber-600 hover:text-amber-800 underline mt-1" data-id="element-29">
                  Dismiss
                </button>
              </div>
            </div>
          </div>}

        <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-30">
          {messages.length === 0 ? <div className="text-center text-gray-500 mt-8" data-id="element-31">
              <p data-id="element-32">Start a conversation about this topic!</p>
              <p className="text-sm mt-2" data-id="element-33">
                The question has been pre-filled below - feel free to edit or
                use as-is.
              </p>
            </div> : messages.map((message, index) => <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-34">
                <div className={`max-w-[80%] rounded-lg p-3 ${message.role === 'user' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-900'}`} data-id="element-35">
                  <ReactMarkdown className="prose prose-sm max-w-none" components={{
              p: ({
                children
              }) => <p className="mb-2 last:mb-0" data-id="element-37">{children}</p>,
              ul: ({
                children
              }) => <ul className="list-disc ml-4 mb-2" data-id="element-38">{children}</ul>,
              ol: ({
                children
              }) => <ol className="list-decimal ml-4 mb-2" data-id="element-39">{children}</ol>,
              li: ({
                children
              }) => <li className="mb-1" data-id="element-40">{children}</li>,
              code: ({
                children
              }) => <code className="bg-black bg-opacity-10 px-1 py-0.5 rounded text-sm" data-id="element-41">
                          {children}
                        </code>,
              pre: ({
                children
              }) => <pre className="bg-black bg-opacity-10 p-2 rounded overflow-x-auto my-2" data-id="element-42">
                          {children}
                        </pre>
            }} data-id="element-36">
                    {message.content}
                  </ReactMarkdown>
                </div>
              </div>)}
          {isLoading && <div className="flex justify-start" data-id="element-43">
              <div className="bg-gray-100 rounded-lg p-3" data-id="element-44">
                <div className="flex gap-1" data-id="element-45">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" data-id="element-46" />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                animationDelay: '0.1s'
              }} data-id="element-47" />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                animationDelay: '0.2s'
              }} data-id="element-48" />
                </div>
              </div>
            </div>}
          <div ref={messagesEndRef} data-id="element-49" />
        </div>

        <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200" data-id="element-50">
          <div className="flex gap-2 items-end" data-id="element-51">
            <textarea ref={textareaRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown} placeholder="Type your message... (Shift+Enter for new line)" rows={3} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none overflow-hidden" disabled={isLoading} style={{
            minHeight: '72px',
            maxHeight: '200px'
          }} data-id="element-52" />
            <button type="submit" disabled={!input.trim() || isLoading} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors h-10" aria-label="Send message" data-id="element-53">
              <SendIcon className="w-5 h-5" data-id="element-54" />
            </button>
          </div>
        </form>
      </div>
    </div>;
}